/**
 * This composable provides state management and keyboard navigation logic for a tabbed interface.
 * It enables users to navigate between tabs using the Arrow and Home/End keys.
 *
 * It is designed to work with a tab structure that uses the following attributes:
 * - `role="tablist"` on the tab container (e.g., `<ul>`).
 * - `role="tab"` on each tab link (e.g., `<a>`).
 * - `data-tab-id` on each tab link, with a value matching the ID in the provided `tabs` array.
 * - `role="tabpanel"` on each tab's content container.
 *
 * @param {Ref<HTMLElement>} container - A reference to the tablist container element.
 * @param {string[] | Ref<string[]>} tabs - An ordered array (or a Ref to an array) of unique string identifiers for each tab.
 * @param {string} initialTab - The identifier of the tab to be active initially.
 * @param {{ defaultFocusTarget?: FocusTarget }} options - Optional behavior overrides for tab activation focus.
 * @returns {{activeTab: import('vue').Ref<string>, changeTab: (newTab: string, focusTarget?: FocusTarget) => Promise<void>, getTabAttributes: TabAttributesGetter}}
 * - `activeTab`: A ref for the currently active tab ID.
 * - `changeTab`: A function to update the active tab.
 * - `getTabAttributes`: A helper function to return tabindex and aria-selected for a given tab ID.
 */
import { ref, watch, onUnmounted, Ref, isRef, toRef, nextTick } from "vue";
import { FOCUS_QUERY, isElementVisible } from "./focusHelpers";

export type FocusTarget = "none" | "tab" | "panel";
type AccessibleTabsOptions = {
    defaultFocusTarget?: FocusTarget;
};

export function useAccessibleTabs(container: Ref<HTMLElement | null>, tabs: string[] | Ref<string[]>, initialTab: string, options: AccessibleTabsOptions = {}) {
    const activeTab = ref(initialTab);
    const defaultFocusTarget = options.defaultFocusTarget ?? "none";
    const focusTab = (tabId: string) => {
        const nextTabElement = container.value?.querySelector(`[data-tab-id="${tabId}"]`) as HTMLElement | null;
        nextTabElement?.focus();
    };
    const focusPanel = async (tabId: string) => {
        await nextTick();
        const panelElement = document.getElementById(tabId);
        if (panelElement instanceof HTMLElement) {
            if (!panelElement.hasAttribute("tabindex")) {
                panelElement.setAttribute("tabindex", "-1");
            }
            const focusablePanelElement = Array.from(panelElement.querySelectorAll(FOCUS_QUERY)).find(isElementVisible) as HTMLElement | undefined;
            if (focusablePanelElement != null) {
                focusablePanelElement.focus();
                if (document.activeElement instanceof HTMLElement && panelElement.contains(document.activeElement)) {
                    return;
                }
            }
            panelElement.focus();
        }
    };
    const changeTab = async (newTab: string, focusTarget: FocusTarget = defaultFocusTarget) => {
        activeTab.value = newTab;
        if (focusTarget === "tab") {
            await nextTick();
            focusTab(newTab);
        } else if (focusTarget === "panel") {
            await focusPanel(newTab);
        }
    };
    const tabsComputed = isRef(tabs) ? tabs : toRef(tabs);

    const getTabAttributes = (tabId: string) => {
        const isActive = activeTab.value === tabId;
        return {
            role: "tab",
            tabindex: isActive ? 0 : -1,
            "data-tab-id": tabId,
            "aria-selected": isActive,
            "aria-controls": tabId,
        };
    };

    const getPanelAttributes = (tabId: string) => {
        return {
            id: tabId,
            role: "tabpanel",
        };
    };

    const handleKeydown = (event: KeyboardEvent) => {
        const { key, target } = event;
        if ((target instanceof Element && !target.matches("[role='tab'][data-tab-id]:not([data-tab-id=''])")) || !["ArrowUp", "ArrowRight", "ArrowDown", "ArrowLeft", "Home", "End"].includes(key)) {
            return;
        }
        const currentIndex = tabsComputed.value.indexOf(activeTab.value);

        let nextIndex = 0;
        if (["ArrowDown", "ArrowRight"].includes(key)) {
            nextIndex = (currentIndex + 1) % tabsComputed.value.length;
        } else if (["ArrowUp", "ArrowLeft"].includes(key)) {
            nextIndex = (currentIndex - 1 + tabsComputed.value.length) % tabsComputed.value.length;
        } else if (key === "Home") {
            nextIndex = 0;
        } else if (key === "End") {
            nextIndex = tabsComputed.value.length - 1;
        }

        event.preventDefault();
        const nextTabId = tabsComputed.value[nextIndex];
        void changeTab(nextTabId, "tab");
    };

    let containerElement: HTMLElement | null = null;
    watch(
        container,
        (newContainer, oldContainer) => {
            if (oldContainer) {
                oldContainer.removeEventListener("keydown", handleKeydown);
                containerElement = null;
            }
            if (newContainer) {
                newContainer.addEventListener("keydown", handleKeydown);
                containerElement = newContainer;
            }
        },
        {
            flush: "post",
            immediate: true,
        },
    );

    onUnmounted(() => {
        containerElement?.removeEventListener("keydown", handleKeydown);
    });

    return {
        activeTab,
        changeTab,
        getTabAttributes,
        getPanelAttributes,
    };
}
